import { FC, memo, useMemo, useState } from 'react'
import { Form, Input, Drawer, Switch } from 'antd'
import styles from './index.module.scss'
import { useTranslation } from '@/services/i18n'
import { ReactComponent as CloseModalIcon } from '@/assets/icons/closeModal.svg'
import { ReactComponent as ArrowLeftIcon } from '@/assets/icons/arrowLeft.svg'
import { useGetNonceLimit, useValidateNonce } from '@/hooks/tx'
import DrawerFooter from '@/components/DrawerFooter'
import Modal from '@/components/Modal'
import { showNonceTip } from '@/utils/tx'

import { useCoinBasis } from '@/hooks/coin'

type AdvancedSettingType = 'evm' | 'btc'

interface AdvancedSettingProps {
  settingType: AdvancedSettingType
  open: boolean
  onClose: () => void
  coinKey: string
  address: string
  nonce: string
  onSaveNonce: (val: string) => void
  isRbf: boolean
  setIsRbf: (val: boolean) => void
}

type SettingProps = Omit<AdvancedSettingProps, 'open' | 'settingType'>

const EVMSetting: FC<SettingProps> = ({ coinKey, address, onClose, onSaveNonce }) => {
  const { t } = useTranslation()

  const coinBasis = useCoinBasis(coinKey)

  const nonceAlias = useMemo(
    () => (coinBasis?.coinImplTag === 'aptos' ? 'SequenceNumber' : 'Nonce'),
    [coinBasis?.coinImplTag]
  )

  const { nonce, nonceLimit, setNonce } = useGetNonceLimit(coinKey, address)
  const { isValid, errMsg } = useValidateNonce(nonce, nonceLimit!, nonceAlias)

  const onSave = () => {
    const onOk = () => {
      onSaveNonce(nonce)
      onClose()
    }
    if (showNonceTip(nonce, nonceLimit!)) {
      Modal.confirm({
        title: t('tx.nonceConfirmTitle'),
        content: t('tx.nonceConfirmContent', { x: nonceAlias }),
        onOk,
      })
      return
    }
    onOk()
  }
  return (
    <>
      <Form layout="vertical">
        <Form.Item label={nonceAlias} help={errMsg} validateStatus={errMsg && 'error'}>
          <Input
            value={nonce}
            onChange={evt => {
              setNonce(evt.target.value)
            }}
          />
        </Form.Item>
      </Form>
      <DrawerFooter onCancel={() => onClose()} onOk={onSave} disabledOk={!isValid || !nonce} />
    </>
  )
}

const BTCSetting: FC<SettingProps> = ({ onClose, isRbf, setIsRbf }) => {
  const { t } = useTranslation()
  const [isOpenedRbf, setOpenedRbf] = useState(isRbf)
  function handleSwitch(checked: boolean) {
    setOpenedRbf(checked)
  }
  return (
    <>
      <div className={styles.rbf}>
        <div className={styles.inputLike}>
          <div>RBF(Replace-By-Fee)</div>
          <Switch checked={isOpenedRbf} onChange={checked => handleSwitch(checked)} />
        </div>
      </div>
      <p className={styles.rbfTip}>{t('wallet.sendDrawer.rbfTip')}</p>
      <p className={styles.rbfTip}>{t('wallet.sendDrawer.rbfTip2')}</p>

      <DrawerFooter
        onCancel={() => onClose()}
        onOk={() => {
          setIsRbf(isOpenedRbf)
          onClose()
        }}
      />
    </>
  )
}

const AdvancedSetting: FC<AdvancedSettingProps> = props => {
  const { settingType, open, onClose, ...args } = props
  const { t } = useTranslation()
  let content = null
  switch (settingType) {
    case 'evm':
      content = <EVMSetting {...args} onClose={onClose} />
      break
    case 'btc':
      content = <BTCSetting {...args} onClose={onClose} />
      break
  }
  return (
    <Drawer
      title={t('wallet.sendDrawer.advancedSetting')}
      placement="right"
      open={open}
      width={560}
      closeIcon={<ArrowLeftIcon onClick={onClose} />}
      destroyOnClose={true}
      className={styles.drawer}
      maskClosable={false}
      extra={
        <div onClick={onClose} className={styles.closeIcon}>
          <CloseModalIcon />
        </div>
      }
    >
      {content}
    </Drawer>
  )
}

export default memo(AdvancedSetting)
